import { z } from 'zod';
import { ApiService } from './ApiService';
import { AxiosResponse } from 'axios';

const firebaseFilterSchemaObject = z.object({
  field: z.string(),
  operator: z.string(),
  value: z.string(),
});

const firebaseFilterArraySchema = z
  .array(firebaseFilterSchemaObject)
  .min(1, 'You cant pass an empty filter array');

const firebaseFilterSchema = z.union([
  firebaseFilterSchemaObject,
  firebaseFilterArraySchema,
]);

export const SqlQuerierSchema = z
  .object({
    host: z.string().optional(),
    port: z
      .string()
      .optional()
      .transform(val => Number(val)),
    database: z.string().optional(),
    username: z.string().optional(),
    password: z.string().optional(),
    dbms: z.enum(['SqlServer', 'MySQL', 'PostgreSQL', 'Firebase', 'MongoDB']),
    query: z.string().optional(),
    connectionString: z.string().optional(),
    collection: z.string().optional(),
    filter: z.string().optional(),
    text: z.string().optional(),
    apiKey: z.string().optional(),
    authDomain: z.string().optional(),
    projectId: z.string().optional(),
    storageBucket: z.string().optional(),
    messagingSenderId: z.string().optional(),
    appId: z.string().optional(),
  })
  .passthrough()
  .superRefine((data, ctx) => {
    if (data.dbms === 'Firebase') {
      if (
        !data.apiKey ||
        !data.authDomain ||
        !data.projectId ||
        !data.storageBucket ||
        !data.messagingSenderId ||
        !data.appId
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'All Firebase fields are required',
        });
      }

      if (data.filter) {
        const parsedFilter = firebaseFilterSchema.safeParse(
          JSON.parse(data.filter),
        );

        if (parsedFilter.success === false) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Invalid filter format',
          });
        }
      }
    }

    if (data.dbms === 'MongoDB') {
      if (
        !data.collection ||
        !data.filter ||
        !data.connectionString ||
        !data.database
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'All MongoDB fields are required',
        });
      }
    }

    if (
      data.dbms === 'SqlServer' ||
      data.dbms === 'MySQL' ||
      data.dbms === 'PostgreSQL'
    ) {
      if (
        !data.host ||
        !data.database ||
        !data.username ||
        !data.port ||
        !data.password ||
        !data.dbms ||
        !data.query
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'All Fields are required',
        });
      }
    }
  });

type SqlQuerierFormData = z.output<typeof SqlQuerierSchema>;

export class SqlQuerierService extends ApiService {
  static pathName = '/database';

  static getPathname(dbms: SqlQuerierFormData['dbms']) {
    switch (dbms) {
      case 'Firebase':
        return '/firebase';
      case 'MongoDB':
        return '/mongo';
      case 'SqlServer':
        return '/sqlserver';
      case 'PostgreSQL':
        return '/postgres';
      case 'MySQL':
        return '/mysql';
      default:
        return '/mysql';
    }
  }

  static sendQuery = (
    data: SqlQuerierFormData,
  ): Promise<AxiosResponse<any>> => {
    const pathname = this.getPathname(data.dbms);

    if (data.dbms === 'Firebase' && data.filter) {
      data.filter = JSON.parse(data.filter);
    }

    return ApiService.post(`${this.pathName}${pathname}`, data);
  };
}
